import { useMemo, useState, useCallback } from 'react';
import { useQuery } from 'react-apollo';

import { ActiveInlineCommentsQuery } from '@confluence/inline-comments-queries';
import type {
	ActiveInlineCommentsQueryType,
	ActiveInlineCommentsQueryVariables,
} from '@confluence/inline-comments-queries';
import { useUnreadInlineComments } from '@confluence/unread-comments';

import { useCommentsData, type AnnotationStatus } from '../useCommentsData';
import { updateCommentsDataState } from '../helper/commentsDataHelper';

export const COMMENT_BATCH_SIZE = 15;

// gets comments using paginated query
export const useActiveInlineCommentsQuery = ({
	pageId,
	markerRefList,
	orderedActiveAnnotationIdList,
	skip = false,
	batchSize,
}: {
	pageId: string;
	markerRefList: string[];
	orderedActiveAnnotationIdList: AnnotationStatus[];
	skip?: boolean;
	batchSize?: number;
}) => {
	const [, { updateIsLoadedStatus, updateInlineCommentsDataMap }] = useCommentsData();

	const [{ readCommentsListState }] = useUnreadInlineComments();

	const [startOffset, setStartOffset] = useState(0);
	const [endOffset, setEndOffset] = useState(COMMENT_BATCH_SIZE);

	// Determine the batch size to use
	const numParentCommentsToFetch = batchSize || COMMENT_BATCH_SIZE;

	// Determine if the query should be skipped
	const skipQuery = !pageId || markerRefList.length === 0 || skip;

	// Determine the subset of markers to fetch based on offsets
	const currentMarkerRefList = useMemo(() => {
		return markerRefList.slice(startOffset, endOffset);
	}, [markerRefList, startOffset, endOffset]);

	// Execute the query
	const {
		data,
		loading: isLoading,
		error,
		refetch,
	} = useQuery<ActiveInlineCommentsQueryType, ActiveInlineCommentsQueryVariables>(
		ActiveInlineCommentsQuery,
		{
			variables: {
				pageId,
				inlineMarkerRefList: currentMarkerRefList,
			},
			fetchPolicy: 'network-only', // we always want to fetch the latest data
			skip: skipQuery,
			onCompleted: (data: ActiveInlineCommentsQueryType) => {
				updateCommentsDataState({
					data,
					readCommentsListState,
					updateInlineCommentsDataMap,
					updateIsLoadedStatus,
				});
			},
		},
	);

	// Function to fetch more annotations before the current annotation
	const fetchMoreAnnotationsBefore = useCallback(async () => {
		if (!isLoading) {
			setStartOffset((prevOffset) => Math.max(0, prevOffset - numParentCommentsToFetch));
			await refetch();
		}
	}, [refetch, numParentCommentsToFetch, isLoading]);

	// Function to fetch more annotations after the current annotation
	const fetchMoreAnnotationsAfter = useCallback(async () => {
		if (!isLoading) {
			setEndOffset((prevOffset) =>
				Math.min(orderedActiveAnnotationIdList.length, prevOffset + numParentCommentsToFetch),
			);
			await refetch();
		}
	}, [refetch, numParentCommentsToFetch, orderedActiveAnnotationIdList.length, isLoading]);

	return {
		isLoading,
		data,
		error,
		fetchMoreAnnotationsBefore,
		fetchMoreAnnotationsAfter,
		refetch,
	};
};
